import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { apiClient, ApiError } from "@/api/client";
import { useState, useEffect, useCallback, useRef } from "react";
import type {
  LocalGatewayRouteDeleteResponse,
  LocalGatewayRoutesDeleteResponse,
  LocalGatewayHandoffResponse,
  LocalGatewayRouteHandoffResponse,
  LocalGatewaySetupHandoffResponse,
  StorageDetailResponse,
  StorageDeleteResponse,
  StorageCredentialStateResponse,
  StorageReadinessResponse,
  StorageRuntimeStatusSnapshot,
  StorageSummary,
} from "@/types/api";
import type {
  StorageConnectionKindId,
  StorageConnectionKindsResponse,
  StorageRegistrationProcedure,
} from "@/types/storage-registration";
import { fetchConnectionKinds } from "../storage-registration";
import { normalizeSmbBasePathInput } from "../smb-root";
import {
  clearPendingCommand,
  idempotencyKeyForCommand,
  readPendingCommand,
  requiredIdempotencyHeaders,
  type PendingCommand,
} from "../command-idempotency";
import { useConsoleReadState } from "@/contexts/ConsoleReadState";
import { getActiveOrganizationScopeId } from "@/lib/organization-scope";

const STORAGE_REGISTRATION_COMMAND_SESSION_KEY =
  "k-drive:storage-registration-command";
const STORAGE_DELETE_COMMAND_SESSION_KEY_PREFIX =
  "k-drive:storage-delete-command:";
const LOCAL_GATEWAY_SETUP_COMMAND_SESSION_KEY =
  "k-drive:local-gateway-setup-command";
const LOCAL_GATEWAY_ROUTE_COMMAND_SESSION_KEY_PREFIX =
  "k-drive:local-gateway-route-command:";

function storageDeleteCommandSessionKey(storageId: string) {
  return `${STORAGE_DELETE_COMMAND_SESSION_KEY_PREFIX}${storageId}`;
}

function runtimeStatusFetchFailedSnapshot(): StorageRuntimeStatusSnapshot {
  return {
    runtimeStatusState: "unknown",
    freshnessState: "unknown",
    reasonCode: "runtime_status_fetch_failed",
    retryable: true,
    observedAt: null,
    expiresAt: null,
    source: "pf_unavailable",
  };
}

function runtimeStatusNotReturnedSnapshot(): StorageRuntimeStatusSnapshot {
  return {
    runtimeStatusState: "not_requested",
    freshnessState: "not_requested",
    reasonCode: "runtime_status_not_returned",
    retryable: true,
    observedAt: null,
    expiresAt: null,
    source: "not_requested",
  };
}

export function useStorageList(filters?: Record<string, string>) {
  const state = useConsoleReadState();
  const list = usePaginatedQuery<StorageSummary>("/storages", filters, { fetcher: state.readList });
  const storageIdsKey = list.items.map(storage => storage.storageId).join("\n");
  useEffect(() => {
    const ids = storageIdsKey ? storageIdsKey.split("\n") : [];
    return state.watch(ids, async () => { await list.refresh(false); });
  }, [state, storageIdsKey, list.requestUrl, list.refresh]);
  const { requestUrl: _requestUrl, ...pagination } = list;
  return {
    ...pagination,
    items: list.items.map(storage => ({
      ...storage,
      ...state.configuration(storage.storageId),
      ...(state.runtime(storage.storageId) ? { runtimeStatusSnapshot: state.runtime(storage.storageId) } : {}),
    })),
  };
}

export function useStorageDetail(storageId: string) {
  const sharedState = useConsoleReadState();
  const [storage, setStorage] = useState<StorageDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(storageId));
  const [isReadinessRefreshing, setIsReadinessRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storageRef = useRef<StorageDetailResponse | null>(null);
  const readinessRefreshInFlightRef = useRef<Promise<void> | null>(null);
  const runtimeStatusRefreshInFlightRef = useRef<
    Promise<StorageRuntimeStatusSnapshot | null> | null
  >(null);
  const runtimeStatusRequestGenerationRef = useRef(0);
  const readinessRequestSeqRef = useRef(0);

  const updateStorage = useCallback((value: StorageDetailResponse | null) => {
    storageRef.current = value;
    setStorage(value);
  }, []);

  const nextReadinessRequestSeq = useCallback(() => {
    readinessRequestSeqRef.current += 1;
    return readinessRequestSeqRef.current;
  }, []);

  const isCurrentReadinessRequest = useCallback(
    (requestSeq: number) => requestSeq === readinessRequestSeqRef.current,
    [],
  );

  const requestRuntimeStatusSnapshot = useCallback(async () => {
    await sharedState.refreshRuntime();
    return sharedState.runtime(storageId) ?? runtimeStatusNotReturnedSnapshot();
  }, [sharedState, storageId]);

  const fetchRuntimeStatusSnapshot = useCallback(async () => {
    if (runtimeStatusRefreshInFlightRef.current) {
      return runtimeStatusRefreshInFlightRef.current;
    }

    const requestGeneration = runtimeStatusRequestGenerationRef.current;
    const request = (async () => {
      try {
        const runtimeStatusSnapshot = await requestRuntimeStatusSnapshot();
        if (runtimeStatusRequestGenerationRef.current !== requestGeneration) {
          return null;
        }
        return runtimeStatusSnapshot;
      } catch {
        if (runtimeStatusRequestGenerationRef.current !== requestGeneration) {
          return null;
        }
        return runtimeStatusFetchFailedSnapshot();
      }
    })();

    runtimeStatusRefreshInFlightRef.current = request;
    void request.finally(() => {
      if (runtimeStatusRefreshInFlightRef.current === request) {
        runtimeStatusRefreshInFlightRef.current = null;
      }
    });

    return request;
  }, [requestRuntimeStatusSnapshot]);

  const mergeRuntimeStatusSnapshot = useCallback(
    (
      current: StorageDetailResponse,
      runtimeStatusSnapshot: StorageRuntimeStatusSnapshot,
    ): StorageDetailResponse => ({
      ...current,
      runtimeStatusSnapshot,
    }),
    [],
  );

  const mergeReadiness = useCallback(
    (
      current: StorageDetailResponse,
      readiness: StorageReadinessResponse,
    ): StorageDetailResponse => ({
      ...current,
      displayName: readiness.displayName,
      storageLocationDisplay: readiness.storageLocationDisplay,
      providerFamilyDisplayLabel: readiness.providerFamilyDisplayLabel,
      registrationProcedure: readiness.registrationProcedure,
      settingState: readiness.settingState,
      settingBlockedReason: readiness.settingBlockedReason,
      recoveryOwner: readiness.recoveryOwner,
      requiredRecoveryAction: readiness.requiredRecoveryAction,
      snapshotVersion: readiness.snapshotVersion,
      storageLifecycleState: readiness.storageLifecycleState,
      settingOwnerUserId: readiness.settingOwnerUserId,
      unusedStorage: readiness.unusedStorage,
      platformStorageState: readiness.platformStorageState,
      platformStorageReadiness: readiness.platformStorageReadiness,
      localGatewaySetupState: readiness.localGatewaySetupState,
      latestLocalGatewayHandoffSummary: readiness.latestLocalGatewayHandoffSummary,
      publicationSummary: readiness.publicationSummary,
      smbProtocolReachabilitySummary: readiness.smbProtocolReachabilitySummary,
      credentialSummary: readiness.credentialSummary,
      operabilityWarnings: readiness.operabilityWarnings,
      requiredGatewayVersionRange: readiness.requiredGatewayVersionRange,
      currentGatewayVersion: readiness.currentGatewayVersion,
      gatewayRouteSummary: readiness.gatewayRouteSummary,
      updatedAt: readiness.updatedAt,
    }),
    [],
  );

  const markRuntimeStatusUnavailable = useCallback(
    (current: StorageDetailResponse): StorageDetailResponse => ({
      ...current,
      runtimeStatusSnapshot: runtimeStatusFetchFailedSnapshot(),
      operabilityWarnings: [
        ...new Set([...(current.operabilityWarnings ?? []), "platform_sync_failed"]),
      ],
    }),
    [],
  );

  const applyReadiness = useCallback(
    async (
      readiness: StorageReadinessResponse,
      requestSeq: number,
      baseStorage?: StorageDetailResponse,
    ) => {
      const current = baseStorage ?? storageRef.current;
      if (!current || !isCurrentReadinessRequest(requestSeq)) {
        return;
      }
      const merged = mergeReadiness(current, readiness);
      updateStorage(merged);
      const runtimeStatusSnapshot = await fetchRuntimeStatusSnapshot();
      if (!isCurrentReadinessRequest(requestSeq)) {
        return;
      }
      if (runtimeStatusSnapshot) {
        updateStorage(mergeRuntimeStatusSnapshot(merged, runtimeStatusSnapshot));
      }
      setError(null);
    },
    [
      fetchRuntimeStatusSnapshot,
      isCurrentReadinessRequest,
      mergeReadiness,
      mergeRuntimeStatusSnapshot,
      updateStorage,
    ],
  );

  const refreshReadiness = useCallback(
    (baseStorage?: StorageDetailResponse): Promise<void> => {
      const current = baseStorage ?? storageRef.current;
      if (!storageId || !current) {
        return Promise.resolve();
      }

      if (readinessRefreshInFlightRef.current) {
        return readinessRefreshInFlightRef.current;
      }

      setIsReadinessRefreshing(true);
      const requestSeq = nextReadinessRequestSeq();
      const request = (async () => {
        try {
          const readiness = await sharedState.readReadiness(storageId);
          await applyReadiness(readiness, requestSeq, current);
        } catch (err) {
          if (!isCurrentReadinessRequest(requestSeq)) {
            return;
          }
          sharedState.markRuntimeFailed(storageId);
          sharedState.publishConfiguration(markRuntimeStatusUnavailable({ ...current, ...sharedState.configuration(storageId) }));
          updateStorage(markRuntimeStatusUnavailable(current));
          setError(err instanceof Error ? err.message : "Fetch failed");
        }
      })();

      readinessRefreshInFlightRef.current = request;
      void request.finally(() => {
        if (readinessRefreshInFlightRef.current === request) {
          readinessRefreshInFlightRef.current = null;
          if (isCurrentReadinessRequest(requestSeq)) {
            setIsReadinessRefreshing(false);
          }
        }
      });

      return request;
    },
    [
      applyReadiness,
      isCurrentReadinessRequest,
      markRuntimeStatusUnavailable,
      nextReadinessRequestSeq,
      sharedState,
      storageId,
      updateStorage,
    ],
  );

  const fetchDetail = useCallback(async () => {
    if (!storageId) {
      updateStorage(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    try {
      const data = await sharedState.readDetail(storageId);
      updateStorage(data);
      const runtimeStatusSnapshot = await fetchRuntimeStatusSnapshot();
      if (runtimeStatusSnapshot) {
        updateStorage(mergeRuntimeStatusSnapshot(data, runtimeStatusSnapshot));
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fetch failed");
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchRuntimeStatusSnapshot,
    mergeRuntimeStatusSnapshot,
    storageId,
    sharedState,
    updateStorage,
  ]);

  const fetchData = useCallback(async (readiness?: StorageReadinessResponse) => {
    if (readiness) {
      sharedState.publishConfiguration(readiness);
      const requestSeq = nextReadinessRequestSeq();
      readinessRefreshInFlightRef.current = null;
      setIsReadinessRefreshing(false);
      await applyReadiness(readiness, requestSeq);
      return;
    }
    if (!storageRef.current) {
      await fetchDetail();
      return;
    }
    await refreshReadiness();
  }, [applyReadiness, fetchDetail, nextReadinessRequestSeq, refreshReadiness, sharedState]);

  useEffect(() => {
    runtimeStatusRequestGenerationRef.current += 1;
    runtimeStatusRefreshInFlightRef.current = null;
  }, [storageId]);

  useEffect(() => {
    if (!storageId) {
      updateStorage(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    sharedState.readDetail(storageId)
      .then((data) => {
        if (!cancelled) {
          updateStorage(data);
          setError(null);
        }
        return fetchRuntimeStatusSnapshot().then((runtimeStatusSnapshot) => ({
          data,
          runtimeStatusSnapshot,
        }));
      })
      .then(({ data, runtimeStatusSnapshot }) => {
        if (!cancelled && runtimeStatusSnapshot) {
          updateStorage(mergeRuntimeStatusSnapshot(data, runtimeStatusSnapshot));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Fetch failed");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    fetchRuntimeStatusSnapshot,
    mergeRuntimeStatusSnapshot,
    storageId,
    sharedState,
    updateStorage,
  ]);

  useEffect(() => {
    if (!storageId) return;
    return sharedState.watch([storageId], () => sharedState.readDetail(storageId));
  }, [sharedState, storageId]);

  return {
    storage: storage ? { ...storage, ...sharedState.configuration(storageId), ...(sharedState.runtime(storageId) ? { runtimeStatusSnapshot: sharedState.runtime(storageId) } : {}) } : null,
    isLoading,
    isReadinessRefreshing,
    error,
    refresh: fetchData,
  };
}

export function useConnectionKinds() {
  const [connectionKinds, setConnectionKinds] = useState<
    StorageConnectionKindsResponse["connectionKinds"]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchConnectionKinds();
      setConnectionKinds(response.connectionKinds);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fetch failed");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchConnectionKinds()
      .then((response) => {
        if (!cancelled) {
          setConnectionKinds(response.connectionKinds);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Fetch failed");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { connectionKinds, isLoading, error, refresh };
}

export function useCreateStorage() {
  const organizationScopeId = getActiveOrganizationScopeId();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingCommandRef = useRef<PendingCommand | null>(
    readPendingCommand(
      STORAGE_REGISTRATION_COMMAND_SESSION_KEY,
      organizationScopeId,
    ),
  );

  const mutate = useCallback(
    async (input: {
      connectionKindId: StorageConnectionKindId;
      registrationProcedure: StorageRegistrationProcedure;
      resourceLocatorInput?: string;
      smbHost?: string;
      smbShareName?: string;
      smbBasePath?: string;
      displayName: string;
    }): Promise<StorageSummary> => {
      setIsLoading(true);
      setError(null);
      try {
        if (input.registrationProcedure === "browse_select_cloud") {
          throw new Error("browse_select_cloud must use browse session flow");
        }

        if (input.registrationProcedure === "browse_select_local") {
          throw new Error(
            "browse_select_local must use local gateway handoff flow",
          );
        }

        if (input.registrationProcedure === "register_existing") {
          const smbBasePath = normalizeSmbBasePathInput(input.smbBasePath);
          const body = {
            connectionKindId: input.connectionKindId,
            registrationProcedure: "register_existing" as const,
            ...(input.connectionKindId === "smb"
              ? {
                  smbRoot: {
                    host: input.smbHost?.trim(),
                    shareName: input.smbShareName?.trim(),
                    ...(smbBasePath ? { basePath: smbBasePath } : {}),
                  },
                }
              : { resourceLocatorInput: input.resourceLocatorInput }),
            storageDisplayName: input.displayName.trim(),
          };
          const idempotencyKey = idempotencyKeyForCommand(
            pendingCommandRef,
            body,
            STORAGE_REGISTRATION_COMMAND_SESSION_KEY,
            organizationScopeId,
          );
          const result = await apiClient<StorageSummary>("/storage-registrations", {
            method: "POST",
            headers: requiredIdempotencyHeaders(idempotencyKey),
            body: JSON.stringify(body),
          });
          pendingCommandRef.current = null;
          clearPendingCommand(
            STORAGE_REGISTRATION_COMMAND_SESSION_KEY,
            organizationScopeId,
          );
          return result;
        }

        const body = {
          connectionKindId: input.connectionKindId,
          registrationProcedure: "new_create" as const,
          storageDisplayName: input.displayName.trim(),
        };
        const idempotencyKey = idempotencyKeyForCommand(
          pendingCommandRef,
          body,
          STORAGE_REGISTRATION_COMMAND_SESSION_KEY,
          organizationScopeId,
        );
        const result = await apiClient<StorageSummary>("/storage-registrations", {
          method: "POST",
          headers: requiredIdempotencyHeaders(idempotencyKey),
          body: JSON.stringify(body),
        });
        pendingCommandRef.current = null;
        clearPendingCommand(
          STORAGE_REGISTRATION_COMMAND_SESSION_KEY,
          organizationScopeId,
        );
        return result;
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [organizationScopeId],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

export function useDeleteStorage(storageId: string) {
  const organizationScopeId = getActiveOrganizationScopeId();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deleteCommandSessionKey = storageDeleteCommandSessionKey(storageId);
  const pendingDeleteCommandRef = useRef<PendingCommand | null>(
    readPendingCommand(deleteCommandSessionKey, organizationScopeId),
  );

  const mutate = useCallback(
    async (input?: {
      deleteAction?:
        | "normal_delete"
        | "force_delete_unused"
        | "confirm_delete_with_local_gateway_routes_delete"
        | "force_delete_unused_with_local_gateway_routes_delete"
        | "force_delete_broken_reference";
    }) => {
      const deleteAction = input?.deleteAction ?? "normal_delete";
      const idempotencyKey = idempotencyKeyForCommand(
        pendingDeleteCommandRef,
        { storageId, deleteAction },
        deleteCommandSessionKey,
        organizationScopeId,
      );
      setIsLoading(true);
      setError(null);
      try {
        const result = await apiClient<StorageDeleteResponse>(
          `/storages/${storageId}?deleteAction=${encodeURIComponent(deleteAction)}`,
          {
            method: "DELETE",
            headers: requiredIdempotencyHeaders(idempotencyKey),
          },
        );
        if (result.deleted) {
          pendingDeleteCommandRef.current = null;
          clearPendingCommand(deleteCommandSessionKey, organizationScopeId);
        }
        return result;
      } catch (err) {
        const confirmation = err instanceof ApiError && [
          "storage_delete_broken_reference_confirmation_required",
          "storage_delete_local_gateway_routes_confirmation_required",
        ].includes(err.code);
        setError(confirmation ? null : "ストレージの削除に失敗しました。削除画面で詳細を確認してください。");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [deleteCommandSessionKey, organizationScopeId, storageId],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

export function useUpdateStorageDisplayName(storageId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (input: { storageDisplayName: string }): Promise<StorageDetailResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<StorageDetailResponse>(
          `/storages/${storageId}/display-name`,
          {
            method: "PUT",
            body: JSON.stringify({
              storageDisplayName: input.storageDisplayName.trim(),
            }),
          },
        );
      } catch (err) {
        const message =
          err instanceof ApiError
            ? formatStorageDisplayNameUpdateError(err)
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [storageId],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

function formatStorageDisplayNameUpdateError(error: ApiError) {
  switch (error.code) {
    case "storage_display_name_invalid":
      return "ストレージ名は1文字以上120文字以内で入力してください。";
    case "storage_owner_mismatch":
      return "このストレージの設定者だけがストレージ名を変更できます。";
    case "storage_not_found":
      return "ストレージが見つかりません。画面を更新してください。";
    default:
      return error.message;
  }
}

export function useUpdateSmbRoot(storageId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (input: {
      host: string;
      shareName: string;
      basePath?: string | null;
    }): Promise<StorageDetailResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<StorageDetailResponse>(
          `/storages/${storageId}/smb-root`,
          {
            method: "PUT",
            body: JSON.stringify({
              host: input.host.trim(),
              shareName: input.shareName.trim(),
              basePath: normalizeSmbBasePathInput(input.basePath),
            }),
          },
        );
      } catch (err) {
        const message = formatSmbRootUpdateError(err);
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [storageId],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

function formatSmbRootUpdateError(error: unknown) {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "invalid_smb_root":
        return "SMB 接続先の形式が正しくありません。ホスト名、共有名、共有内の / から始まるパスを確認してください。";
      case "smb_registration_detail_missing":
        return "このストレージの SMB 登録情報が不足しているため、接続先情報を更新できません。";
      case "storage_owner_mismatch":
        return "このストレージの設定者だけが接続先情報を更新できます。";
      case "unsupported_storage_class":
        return "SMB ストレージ以外では接続先情報を更新できません。";
      case "platform_control_plane_request_failed":
        return "接続先情報を更新できませんでした。時間をおいて再試行するか、状態を再確認してください。";
      case "platform_control_plane_unavailable":
        return "サービスに接続できないため、接続先情報を更新できませんでした。時間をおいて再試行してください。";
      default:
        return error.message;
    }
  }
  return error instanceof Error ? error.message : "Unknown error";
}

export function useDeleteLocalGatewayRoute(storageId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (input: { routeId: string }): Promise<LocalGatewayRouteDeleteResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<LocalGatewayRouteDeleteResponse>(
          `/storages/${storageId}/local-gateway-routes/${encodeURIComponent(input.routeId)}`,
          {
            method: "DELETE",
          },
        );
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [storageId],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

export function useDeleteAllLocalGatewayRoutes(storageId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutate = useCallback(async (): Promise<LocalGatewayRoutesDeleteResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      return await apiClient<LocalGatewayRoutesDeleteResponse>(
        `/storages/${storageId}/local-gateway-routes`,
        { method: "DELETE" },
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [storageId]);
  const reset = useCallback(() => setError(null), []);
  return { mutate, isLoading, error, reset };
}

export function useIssueLocalGatewaySetupHandoff() {
  const organizationScopeId = getActiveOrganizationScopeId();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingCommandRef = useRef<PendingCommand | null>(
    readPendingCommand(
      LOCAL_GATEWAY_SETUP_COMMAND_SESSION_KEY,
      organizationScopeId,
    ),
  );

  const mutate = useCallback(
    async (input: {
      connectionKindId: "local_folder";
      storageId?: string;
      displayName?: string;
    }): Promise<LocalGatewaySetupHandoffResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        const body = {
          connectionKindId: input.connectionKindId,
          ...(input.storageId ? { storageId: input.storageId } : {}),
          ...(input.displayName ? { storageDisplayName: input.displayName } : {}),
        };
        const idempotencyKey = idempotencyKeyForCommand(
          pendingCommandRef,
          body,
          LOCAL_GATEWAY_SETUP_COMMAND_SESSION_KEY,
          organizationScopeId,
        );
        const result = await apiClient<LocalGatewaySetupHandoffResponse>(
          "/local-gateway-setup-handoffs",
          {
            method: "POST",
            headers: requiredIdempotencyHeaders(idempotencyKey),
            body: JSON.stringify(body),
          },
        );
        pendingCommandRef.current = null;
        clearPendingCommand(
          LOCAL_GATEWAY_SETUP_COMMAND_SESSION_KEY,
          organizationScopeId,
        );
        return result;
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [organizationScopeId],
  );

  return { mutate, isLoading, error };
}

export function useIssueLocalGatewayRouteHandoff() {
  const organizationScopeId = getActiveOrganizationScopeId();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingCommandRef = useRef<PendingCommand | null>(null);

  const mutate = useCallback(
    async (input: {
      storageId: string;
    }): Promise<LocalGatewayRouteHandoffResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        const body = { storageId: input.storageId };
        const sessionKey =
          `${LOCAL_GATEWAY_ROUTE_COMMAND_SESSION_KEY_PREFIX}${input.storageId}`;
        if (!pendingCommandRef.current) {
          pendingCommandRef.current = readPendingCommand(
            sessionKey,
            organizationScopeId,
          );
        }
        const idempotencyKey = idempotencyKeyForCommand(
          pendingCommandRef,
          body,
          sessionKey,
          organizationScopeId,
        );
        const result = await apiClient<LocalGatewayRouteHandoffResponse>(
          `/storages/${input.storageId}/local-gateway-route-handoffs`,
          {
            method: "POST",
            headers: requiredIdempotencyHeaders(idempotencyKey),
          },
        );
        pendingCommandRef.current = null;
        clearPendingCommand(sessionKey, organizationScopeId);
        return result;
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [organizationScopeId],
  );

  return { mutate, isLoading, error };
}

export function usePutStorageCredentialMaterial(storageId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (input: {
      authMethod?: "ntlmv2" | "kerberos" | "kerberos_ntlmv2";
      kerberos?: { realm: string; kdcHost: string; disableRequestIntegrity?: boolean };
      username: string;
      password: string;
      domain?: string;
    }): Promise<StorageCredentialStateResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<StorageCredentialStateResponse>(
          `/storages/${storageId}/credential/material`,
          {
            method: "PUT",
            body: JSON.stringify({
              credentialMaterialKind: "smb_password_v1",
              credentialMaterial: {
                username: input.username,
                password: input.password,
                authMethod: input.authMethod ?? "ntlmv2",
                ...((input.authMethod === "kerberos" || input.authMethod === "kerberos_ntlmv2")
                  ? { kerberos: input.kerberos, ...(input.authMethod === "kerberos_ntlmv2" ? { domain: input.domain } : {}) }
                  : { domain: input.domain?.trim() ? input.domain.trim() : null }),
              },
              verificationMode: "verify_now",
            }),
          },
        );
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [storageId],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

export function useVerifyStorageCredential(storageId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (): Promise<StorageCredentialStateResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      return await apiClient<StorageCredentialStateResponse>(
        `/storages/${storageId}/credential/verify`,
        {
          method: "POST",
        },
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [storageId]);

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

export function useResetStorageCredential(storageId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (): Promise<StorageCredentialStateResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      return await apiClient<StorageCredentialStateResponse>(
        `/storages/${storageId}/credential/reset`,
        {
          method: "POST",
        },
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [storageId]);

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}

const LOCAL_SETUP_POLL_INTERVAL_MS = 3_000;
const LOCAL_SETUP_POLL_TIMEOUT_MS = 5 * 60 * 1000;

export type LocalSetupPollingStatus = "idle" | "polling" | "timed_out";
export type IssuedLocalGatewayHandoffPurpose =
  | "local_setup"
  | "gateway_route_attach";

export interface IssuedLocalGatewayHandoffPollingOptions {
  purpose?: IssuedLocalGatewayHandoffPurpose;
  initialActiveRouteCount?: number;
  initialRouteIds?: readonly string[];
  onDiscardHandoff?: (storageId: string) => void;
}

function isIssuedHandoffExpired(handoff: LocalGatewayHandoffResponse) {
  const expiresAtMs = Date.parse(handoff.expiresAt);
  return Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now();
}

function hasNewGatewayRoute(
  readiness: StorageReadinessResponse,
  options: IssuedLocalGatewayHandoffPollingOptions,
) {
  const activeRouteCount = readiness.gatewayRouteSummary?.activeRouteCount ?? 0;
  if (
    typeof options.initialActiveRouteCount === "number" &&
    activeRouteCount > options.initialActiveRouteCount
  ) {
    return true;
  }

  if (options.initialRouteIds) {
    const initialRouteIds = new Set(options.initialRouteIds);
    return (readiness.gatewayRouteSummary?.routes ?? []).some(
      (route) =>
        route.lifecycle === "active" && !initialRouteIds.has(route.routeId),
    );
  }

  return activeRouteCount > 0;
}

function hasConsumedLocalGatewayHandoff(
  readiness: StorageReadinessResponse,
  options: IssuedLocalGatewayHandoffPollingOptions,
) {
  const localGatewaySetupState = readiness.localGatewaySetupState;
  if (readiness.latestLocalGatewayHandoffSummary?.status === "consumed") {
    return true;
  }
  if (options.purpose === "gateway_route_attach") {
    return (
      localGatewaySetupState === "binding_in_progress" ||
      hasNewGatewayRoute(readiness, options)
    );
  }
  return (
    (readiness.gatewayRouteSummary?.activeRouteCount ?? 0) > 0 ||
    localGatewaySetupState === "binding_in_progress" ||
    localGatewaySetupState === "publication_pending" ||
    localGatewaySetupState === "completed" ||
    localGatewaySetupState === "local_setup_completed"
  );
}

function isLatestHandoffExpired(readiness: StorageReadinessResponse) {
  return readiness.latestLocalGatewayHandoffSummary?.status === "expired";
}

const TERMINAL_LOCAL_GATEWAY_HANDOFF_POLLING_STATUSES = new Set([
  401,
  403,
  404,
  410,
]);

const TERMINAL_LOCAL_GATEWAY_HANDOFF_POLLING_CODES = new Set([
  "session_expired",
  "auth_required",
  "forbidden",
  "not_found",
  "storage_not_found",
  "storage_deleted",
  "storage_access_denied",
  "customer_membership_not_found",
  "customer_membership_inactive",
]);

function isTerminalLocalGatewayHandoffPollingError(error: unknown) {
  if (!(error instanceof ApiError)) {
    return false;
  }
  if (TERMINAL_LOCAL_GATEWAY_HANDOFF_POLLING_STATUSES.has(error.status)) {
    return true;
  }
  if (TERMINAL_LOCAL_GATEWAY_HANDOFF_POLLING_CODES.has(error.code)) {
    return true;
  }
  if (error.details?.retryability === "non_retryable") {
    return true;
  }
  const diagnostic = error.details?.diagnostic;
  return (
    typeof diagnostic === "object" &&
    diagnostic !== null &&
    "retryable" in diagnostic &&
    (diagnostic as { retryable?: unknown }).retryable === false
  );
}

function hasActiveIssuedLocalGatewayHandoff(
  storage: StorageDetailResponse | null,
) {
  const summary = storage?.latestLocalGatewayHandoffSummary;
  if (summary?.status !== "issued") {
    return false;
  }
  const expiresAtMs = Date.parse(summary.expiresAt);
  return Number.isFinite(expiresAtMs) && expiresAtMs > Date.now();
}

export function useIssuedLocalGatewayHandoffPolling(
  handoff: LocalGatewayHandoffResponse | null,
  onRegistered: (
    storageId: string,
    readiness: StorageReadinessResponse,
  ) => void,
  options: IssuedLocalGatewayHandoffPollingOptions = {},
) {
  const [status, setStatus] = useState<LocalSetupPollingStatus>("idle");
  const [isExpired, setIsExpired] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const currentHandoffKeyRef = useRef<string | null>(null);
  const elapsedRef = useRef(0);
  const handoffKey = handoff
    ? `${handoff.storageId}:${handoff.registrationToken}:${handoff.expiresAt}`
    : null;
  const initialRouteIdsKey = options.initialRouteIds?.join("\u0000") ?? "";
  const pollingOptionsRef = useRef(options);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    currentHandoffKeyRef.current = handoffKey;
  }, [handoffKey]);

  useEffect(() => {
    pollingOptionsRef.current = options;
  }, [initialRouteIdsKey, options.initialActiveRouteCount, options.purpose, options]);

  const refreshRegistrationStatus = useCallback(() => {
    if (!handoff?.storageId) {
      return Promise.resolve(false);
    }

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    setIsChecking(true);
    const requestHandoffKey = handoffKey;
    const request = (async () => {
      const readiness = await apiClient<StorageReadinessResponse>(
        `/storages/${handoff.storageId}/readiness`,
      );
      if (currentHandoffKeyRef.current !== requestHandoffKey) {
        return false;
      }

      const currentOptions = pollingOptionsRef.current;
      const effectiveOptions: IssuedLocalGatewayHandoffPollingOptions = {
        purpose:
          currentOptions.purpose ??
          (handoff.connectionKindId === "smb"
            ? "gateway_route_attach"
            : "local_setup"),
        initialActiveRouteCount: currentOptions.initialActiveRouteCount,
        initialRouteIds: currentOptions.initialRouteIds,
      };

      if (hasConsumedLocalGatewayHandoff(readiness, effectiveOptions)) {
        currentOptions.onDiscardHandoff?.(handoff.storageId);
        stopPolling();
        setStatus("idle");
        setError(null);
        onRegistered(handoff.storageId, readiness);
        return true;
      }

      const expired = isLatestHandoffExpired(readiness) || isIssuedHandoffExpired(handoff);
      setIsExpired(expired);
      if (expired) {
        currentOptions.onDiscardHandoff?.(handoff.storageId);
        stopPolling();
        setStatus("idle");
      }
      setError(null);
      return false;
    })()
      .catch((err: unknown) => {
        if (currentHandoffKeyRef.current === requestHandoffKey) {
          const message = err instanceof Error ? err.message : "Fetch failed";
          setError(message);
          if (isTerminalLocalGatewayHandoffPollingError(err)) {
            pollingOptionsRef.current.onDiscardHandoff?.(handoff.storageId);
            stopPolling();
            setStatus("idle");
          }
          console.warn("[local-gateway-handoff-polling] refresh failed", {
            storageId: handoff.storageId,
            error: err,
          });
        }
        return false;
      })
      .finally(() => {
        if (currentHandoffKeyRef.current === requestHandoffKey) {
          setIsChecking(false);
        }
        if (inFlightRef.current === request) {
          inFlightRef.current = null;
        }
      });

    inFlightRef.current = request;
    return request;
  }, [
    handoff,
    handoffKey,
    onRegistered,
    stopPolling,
  ]);

  const startPollingInterval = useCallback(() => {
    stopPolling();
    intervalRef.current = setInterval(() => {
      elapsedRef.current += LOCAL_SETUP_POLL_INTERVAL_MS;

      if (elapsedRef.current >= LOCAL_SETUP_POLL_TIMEOUT_MS) {
        stopPolling();
        setStatus("timed_out");
        console.warn(
          `[local-gateway-handoff-polling] timed out after ${LOCAL_SETUP_POLL_TIMEOUT_MS}ms for storage ${handoff?.storageId}`,
        );
        return;
      }

      void refreshRegistrationStatus();
    }, LOCAL_SETUP_POLL_INTERVAL_MS);
  }, [handoff?.storageId, refreshRegistrationStatus, stopPolling]);

  const restartPolling = useCallback(() => {
    if (!handoff?.storageId || isIssuedHandoffExpired(handoff)) {
      return;
    }
    elapsedRef.current = 0;
    setIsExpired(false);
    setError(null);
    setStatus("polling");
    void refreshRegistrationStatus();
    startPollingInterval();
  }, [handoff, refreshRegistrationStatus, startPollingInterval]);

  useEffect(() => {
    stopPolling();
    setError(null);
    setIsChecking(false);
    inFlightRef.current = null;
    elapsedRef.current = 0;

    if (!handoff?.storageId) {
      setIsExpired(false);
      setStatus("idle");
      return;
    }

    const expired = isIssuedHandoffExpired(handoff);
    setIsExpired(expired);
    setStatus(expired ? "idle" : "polling");

    void refreshRegistrationStatus();

    if (expired) {
      return;
    }

    startPollingInterval();

    return () => {
      stopPolling();
    };
  }, [handoff, refreshRegistrationStatus, startPollingInterval, stopPolling]);

  useEffect(() => {
    if (status !== "polling" && status !== "timed_out") {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshRegistrationStatus();
        if (status === "polling") {
          elapsedRef.current = 0;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshRegistrationStatus, status]);

  useEffect(() => {
    return () => {
      currentHandoffKeyRef.current = null;
      stopPolling();
    };
  }, [stopPolling]);

  return {
    pollingStatus: status,
    isExpired,
    isChecking,
    error,
    refreshRegistrationStatus,
    restartPolling,
  };
}

/**
 * Polls the storage detail API while a local-capable storage is in a
 * non-terminal local setup state (DES-WEBUI-014 / REQ-WEBUI-009).
 *
 * Stops automatically after 5 minutes without state change, and resumes
 * a single immediate fetch when the browser tab regains visibility.
 */
export function useLocalSetupPolling(
  storageId: string,
  storage: StorageDetailResponse | null,
  refresh: () => Promise<void>,
) {
  const [status, setStatus] = useState<LocalSetupPollingStatus>("idle");
  const baselineRef = useRef<string | null>(null);
  const elapsedRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  const shouldPoll =
    storage !== null &&
    (storage.connectionKindId === "local_folder" ||
      storage.connectionKindId === "smb") &&
    hasActiveIssuedLocalGatewayHandoff(storage) &&
    storage.settingState === "pending" &&
    !(storage.operabilityWarnings ?? []).includes("platform_sync_failed") &&
    (storage.localGatewaySetupState === "setup_pending" ||
      storage.localGatewaySetupState === "owner_auth_pending" ||
      storage.localGatewaySetupState === "registration_pending" ||
      storage.localGatewaySetupState === "binding_in_progress" ||
      storage.localGatewaySetupState === "publication_pending");

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const restartPolling = useCallback(() => {
    stopPolling();
    elapsedRef.current = 0;
    baselineRef.current = storage?.localGatewaySetupState ?? null;
    setStatus("polling");
  }, [stopPolling, storage?.localGatewaySetupState]);

  const refreshOnce = useCallback(() => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const request = refresh()
      .catch((error: unknown) => {
        // Transient network errors keep polling alive, but log them so a
        // persistently failing tick is observable rather than silent.
        console.warn("[local-setup-polling] refresh failed", {
          storageId,
          error,
        });
      })
      .finally(() => {
        if (refreshInFlightRef.current === request) {
          refreshInFlightRef.current = null;
        }
      });

    refreshInFlightRef.current = request;
    return request;
  }, [refresh, storageId]);

  useEffect(() => {
    if (!shouldPoll) {
      stopPolling();
      setStatus("idle");
      return;
    }

    if (status === "idle") {
      baselineRef.current = storage?.localGatewaySetupState ?? null;
      elapsedRef.current = 0;
      setStatus("polling");
    }
  }, [shouldPoll, status, stopPolling, storage?.localGatewaySetupState]);

  useEffect(() => {
    if (status !== "polling" || !shouldPoll) {
      return;
    }

    const tick = async () => {
      elapsedRef.current += LOCAL_SETUP_POLL_INTERVAL_MS;

      await refreshOnce();

      if (elapsedRef.current >= LOCAL_SETUP_POLL_TIMEOUT_MS) {
        stopPolling();
        setStatus("timed_out");
        console.warn(
          `[local-setup-polling] timed out after ${LOCAL_SETUP_POLL_TIMEOUT_MS}ms for storage ${storageId}`,
        );
      }
    };

    intervalRef.current = setInterval(() => void tick(), LOCAL_SETUP_POLL_INTERVAL_MS);

    return () => {
      stopPolling();
    };
  }, [status, shouldPoll, refreshOnce, stopPolling, storageId]);

  useEffect(() => {
    if (
      status === "polling" &&
      storage?.localGatewaySetupState &&
      baselineRef.current !== null &&
      storage.localGatewaySetupState !== baselineRef.current
    ) {
      baselineRef.current = storage.localGatewaySetupState;
      elapsedRef.current = 0;
    }
  }, [status, storage?.localGatewaySetupState]);

  useEffect(() => {
    if (status !== "polling" && status !== "timed_out") {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshOnce();
        if (status === "polling") {
          elapsedRef.current = 0;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [status, refreshOnce]);

  return { pollingStatus: status, restartPolling };
}
